'use client';

import { BottomDrawer, Portal } from '@common/components';
import { SvgDelete } from '@common/icon';
import { cn } from '@core/utils';
import { AnimatePresence, motion } from 'framer-motion';

import { getMainWrapperElement } from '@/app/_components/MainWrapperPortal';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { ResponsiveDialogConfig } from '@/stores/DrawerStore';

import { DonationModal } from '../molecule/DonationModal';

interface ResponsiveDialogProps {
  opened: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  /** drawer 헤더에 표시될 제목 */
  title?: ResponsiveDialogConfig['title'];
  /** 모달의 header 부분 엘리먼트 */
  modalHeader?: ResponsiveDialogConfig['modalHeader'];
  duration?: number;
  /** bottom 형태인지 full 형태인지 결정 */
  drawerType?: 'bottom' | 'full';
  /** true일 경우 drawer content 영역의 padding 제거 (모바일만 적용) */
  drawerNoPadding?: boolean;
  /** true일 경우 full drawer 기본 헤더 숨기기 */
  hideDrawerHeader?: boolean;
  /** true일 경우 모바일에서도 모달로 표시 */
  forceModal?: boolean;
  /** true일 경우 앱바 스타일 헤더 적용 (왼쪽 X, 가운데 타이틀) */
  isAppBarHeader?: boolean;
  className?: {
    modal?: string;
    backdrop?: string;
    drawer?: string;
  };
}

/**
 * 데스크톱에서는 모달, 모바일에서는 드로워로 표시\
 * 직접 사용하기 보다는 openResponsiveDialog 유틸로 간결하게 사용
 */
export const ResponsiveDialog = ({
  opened,
  onClose,
  children,
  modalHeader,
  title,
  duration = 300,
  drawerType = 'full',
  drawerNoPadding,
  hideDrawerHeader,
  forceModal,
  isAppBarHeader,
  className,
}: ResponsiveDialogProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  if (isDesktopViewport || forceModal) {
    return (
      <DonationModal
        opened={opened}
        onClose={onClose}
        className={className?.modal}
        header={modalHeader}
      >
        {children}
      </DonationModal>
    );
  }

  if (drawerType === 'bottom') {
    return (
      <BottomDrawer
        opened={opened}
        onClose={onClose}
        unmountOnExit
        renderTo={getMainWrapperElement}
        className={{
          backdrop: cn('fixed', className?.backdrop),
          drawer: cn('fixed', className?.drawer),
        }}
      >
        <BottomDrawer.HandleWrapper>
          <BottomDrawer.Handle />
        </BottomDrawer.HandleWrapper>
        <BottomDrawer.Contents
          className={cn({ 'mx-20': !drawerNoPadding, 'mx-0': drawerNoPadding })}
        >
          {title ? (
            <h2 className="mb-28 font-700 text-Gray-900 typo-body1">{title}</h2>
          ) : null}
          {children}
        </BottomDrawer.Contents>
      </BottomDrawer>
    );
  }

  return (
    <FullDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      duration={duration}
      isAppBarHeader={isAppBarHeader}
      drawerNoPadding={drawerNoPadding}
      hideDrawerHeader={hideDrawerHeader}
      className={className}
    >
      {children}
    </FullDrawer>
  );
};

interface FullDrawerProps {
  opened: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  title?: string;
  duration: number;
  isAppBarHeader?: boolean;
  drawerNoPadding?: boolean;
  hideDrawerHeader?: boolean;
  className?: {
    backdrop?: string;
    drawer?: string;
  };
}

const FullDrawer = ({
  opened,
  onClose,
  children,
  title,
  duration,
  isAppBarHeader,
  drawerNoPadding,
  hideDrawerHeader,
  className,
}: FullDrawerProps) => {
  const durationInSeconds = duration / 1000;

  return (
    <Portal renderTo={getMainWrapperElement}>
      <AnimatePresence>
        {opened && (
          <>
            <motion.div
              className={cn(
                'fixed inset-0 z-40 bg-Gray-white',
                className?.backdrop,
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: durationInSeconds }}
              onClick={onClose}
            />
            <motion.div
              className={cn(
                'fixed inset-0 z-50 flex flex-col bg-Gray-white',
                className?.drawer,
              )}
              initial={{ y: '20%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '20%', opacity: 0 }}
              transition={{
                type: 'tween',
                duration: durationInSeconds,
                ease: 'easeInOut',
              }}
            >
              {hideDrawerHeader ? null : (
                <FullDrawerHeader
                  title={title}
                  isAppBarHeader={isAppBarHeader}
                  onClose={onClose}
                />
              )}
              <div
                className={cn('flex-1 overflow-auto', {
                  'px-20': !drawerNoPadding,
                  'px-0': drawerNoPadding,
                })}
              >
                {children}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};

interface FullDrawerHeaderProps {
  title?: string;
  isAppBarHeader?: boolean;
  onClose?: () => void;
}

const FullDrawerHeader = ({
  title,
  isAppBarHeader,
  onClose,
}: FullDrawerHeaderProps) => (
  <header
    className={cn('flex min-h-56 items-center px-20', {
      'justify-between': !isAppBarHeader,
    })}
  >
    {isAppBarHeader ? (
      <>
        <button
          type="button"
          onClick={onClose}
          className="flex size-24 items-center justify-center"
          aria-label="닫기"
        >
          <SvgDelete className="size-24 text-Gray-900" />
        </button>
        <h2 className="flex-1 text-center font-600 text-Gray-900 typo-body1">
          {title || ''}
        </h2>
        <div className="size-24" />
      </>
    ) : (
      <>
        <h2 className="font-700 text-Gray-900 typo-h6">{title || ''}</h2>
        <button
          type="button"
          onClick={onClose}
          className="flex size-32 items-center justify-center"
          aria-label="닫기"
        >
          <SvgDelete />
        </button>
      </>
    )}
  </header>
);
